import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {companyGetAll, companyExcel} from '../../../redux/slices/company.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'

import {CompanyType} from '../../../helpers'

const selectCompanyData = (state) => ({
  company: state.company.company,
  loading: state.company.loading,
})

const selectCategoryData = (state) => ({
  category: state.category.category,
})

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const pageKey = 'CUSTOMERS'

  const {company, loading} = useSelector(selectCompanyData)
  const {category} = useSelector(selectCategoryData)

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) {
      dispatch(companyGetAll({params, companyTypeId: CompanyType.Customer}))
      dispatch(categoryGetAll({SourceTypeId: 11}))
    }
  }, [params, dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        companyExcel({
          params,
          companyTypeId: CompanyType.Customer,
          fileName: intl.formatMessage({id: 'BC.CUSTOMERS'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const Columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'COMPANY.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCellDouble item={props} double='title' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'COMPANY.SEARCH.CATEGORYNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'categoryName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'COMPANY.SEARCH.TAXOFFICENO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'taxOffice',
        Cell: ({...props}) => <CustomCellDouble item={props} double='taxNumber' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'COMPANY.SEARCH.IDENTITYNUMBER'})}
            className='min-w-125px'
          />
        ),
        accessor: 'identityNumber',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'COMPANY.SEARCH.BALANCE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'balance',
        Cell: ({...props}) => <CustomCellDouble item={props} double='balanceInformation' price />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemo(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'FILTER.HASBALANCE',
            data: [
              {
                id: 'true',
                name: intl.formatMessage({id: 'FILTER.HASBALANCE.TRUE'}),
              },
              {
                id: 'false',
                name: intl.formatMessage({id: 'FILTER.HASBALANCE.FALSE'}),
              },
            ],
            name: 'hasBalance',
          },
          {
            title: 'COMPANY.SEARCH.CATEGORYNAME',
            data: category,
            name: 'categoryId',
          },
        ],
      },
    ],
    [intl, category, handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.CUSTOMERS'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={(values) => setParams({...values})}
          pages={pageKey}
        />
        <KTCardBody>
          {!company && <ListLoading />}
          {company && (
            <Table
              items={company}
              cols={Columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
